import { detectInput } from '../../pages/home/homeSearchUtils';

/** Compact pivot CTA — unified cyan ghost style. */
export const PIVOT_CTA_CLASS =
  'shrink-0 inline-flex h-7 items-center justify-center border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 px-2.5 py-1 rounded-md text-xs font-semibold transition';

/**
 * Resolve pivot module for display / launch copy.
 * @param {object} pivot
 * @returns {'email'|'company'|'person'}
 */
export function resolvePivotKind(pivot) {
  const kind = String(pivot?.kind || pivot?.tab || '').toLowerCase();
  if (kind === 'email' || String(pivot?.email || '').includes('@')) return 'email';
  if (kind === 'person' || pivot?.handle || pivot?.person_name) return 'person';
  if (kind === 'company' || kind === 'corporate' || pivot?.domain) return 'company';
  const tab = detectInput(String(pivot?.query || pivot?.label || ''))?.tab;
  if (tab === 'email') return 'email';
  if (tab === 'person') return 'person';
  if (tab === 'company') return 'company';
  const explicit = String(pivot?.action_label || '').trim();
  if (/email/i.test(explicit)) return 'email';
  if (/identity|social|person|handle/i.test(explicit)) return 'person';
  if (/corp|company|due diligence/i.test(explicit)) return 'company';
  return 'company';
}

/**
 * Clean target string shown in the Target line and used for launch preview.
 * @param {object} pivot
 * @returns {string}
 */
export function pivotCleanTarget(pivot) {
  const kind = resolvePivotKind(pivot);
  if (kind === 'email') {
    return String(pivot?.email || pivot?.query || pivot?.label || '').trim();
  }
  if (kind === 'company') {
    const domain = String(pivot?.domain || '').trim();
    if (domain) return domain;
    return String(pivot?.query || pivot?.label || pivot?.company_name || '').trim();
  }
  return String(
    pivot?.handle || pivot?.person_name || pivot?.query || pivot?.label || '',
  )
    .trim()
    .replace(/^@+/, '');
}

/**
 * Human action description (replaces internal source tags like site_enrich).
 * @param {object} pivot
 * @returns {string}
 */
export function pivotActionDescription(pivot) {
  const kind = resolvePivotKind(pivot);
  const target = pivotCleanTarget(pivot) || 'Lead';
  if (kind === 'email') return `Target: ${target} • Email Intelligence`;
  if (kind === 'person') return `Target: ${target} • Identity & Social Search`;
  return `Target: ${target} • Corporate Due Diligence`;
}

/**
 * Short CTA label on the right-hand button — names the exact launch target.
 * @param {object} pivot
 * @returns {string}
 */
export function pivotActionLabel(pivot) {
  const target = pivotCleanTarget(pivot);
  if (target) return `Scan ${target}`;
  const kind = resolvePivotKind(pivot);
  if (kind === 'email') return 'Scan email';
  if (kind === 'person') return 'Scan person';
  return 'Scan corporate';
}

/**
 * Native tooltip preview of the exact query that will be launched.
 * @param {object} pivot
 * @returns {string}
 */
export function pivotLaunchTitle(pivot) {
  const kind = resolvePivotKind(pivot);
  const target = pivotCleanTarget(pivot) || 'Lead';
  if (kind === 'email') return `Launch Email Intelligence for: ${target}`;
  if (kind === 'person') return `Launch Identity & Social Search for: ${target}`;
  return `Launch Corporate Search for: ${target}`;
}

/**
 * Consent and "before you access" interstitials are page chrome, not an entity name.
 * Matched in several languages so a Romanian, French, or English gate title
 * falls back the same way.
 */
const ACCESS_GATE_LABEL_RE = new RegExp(
  [
    '^(?:\\u00ee|i)nainte\\s+de\\s+a\\s+accesa\\b',
    '^before\\s+(?:you\\s+)?(?:access(?:ing)?|continue|proceed)\\b',
    "^avant\\s+d['\\u2019]acc[e\\u00e9]der\\b",
    '^bevor\\s+sie\\b',
    '^antes\\s+de\\s+(?:acceder|continuar)\\b',
    '^prima\\s+di\\s+accedere\\b',
  ].join('|'),
  'i',
);

/**
 * @param {string} host
 * @returns {string}
 */
function hostDisplayName(host) {
  const bare = String(host || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0];
  const root = bare.split('.')[0];
  if (!root) return '';
  return root.charAt(0).toUpperCase() + root.slice(1);
}

/**
 * Bold pivot title. Gate-page titles are replaced with the English host name.
 * @param {object} pivot
 * @returns {string}
 */
export function pivotDisplayLabel(pivot) {
  const raw = String(pivot?.label || pivot?.query || pivot?.email || '').trim();
  if (raw && !ACCESS_GATE_LABEL_RE.test(raw)) return raw;
  const fromHost = hostDisplayName(pivot?.domain);
  if (fromHost) return fromHost;
  const target = pivotCleanTarget(pivot);
  if (target && !ACCESS_GATE_LABEL_RE.test(target)) return target;
  return raw || 'Lead';
}

function pivotEntityIcon(pivot) {
  const kind = resolvePivotKind(pivot);
  if (kind === 'email') return '✉️';
  if (kind === 'person') return '👤';
  return '🏢';
}

/**
 * Compact unified target-pivots list (native dark theme).
 *
 * @param {{
 *   extractedLeads?: { pivots?: object[], emails?: string[] },
 *   onPivotLead?: (pivot: object) => void,
 * }} props
 */
export default function TargetPivotsCard({ extractedLeads, onPivotLead }) {
  const pivots = Array.isArray(extractedLeads?.pivots) ? extractedLeads.pivots : [];
  const leadEmails = Array.isArray(extractedLeads?.emails) ? extractedLeads.emails : [];
  const rows =
    pivots.length > 0
      ? pivots
      : leadEmails.map((email) => ({
          kind: 'email',
          tab: 'email',
          label: email,
          query: email,
          email,
          action_label: 'Email Intelligence',
          source: 'lead_email',
        }));

  if (rows.length === 0) return null;

  return (
    <section
      className="phone-target-pivots-card rounded-xl border border-slate-800 bg-slate-900/80 p-3.5 text-slate-200 shadow-sm animate-in fade-in duration-500"
      aria-label="Discovered Target Pivots"
      data-testid="phone-discovered-pivots"
    >
      <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400/90">
        Target Pivots
      </h4>
      <p className="mt-1 text-xs text-slate-400">
        Linked company, person, and email hypotheses — not confirmed Owner verdicts.
      </p>
      <ul className="mt-3 space-y-1.5">
        {rows.map((pivot, i) => {
          const label = pivotDisplayLabel(pivot);
          const action = pivotActionLabel(pivot);
          const icon = pivotEntityIcon(pivot);
          const description = pivotActionDescription(pivot);
          const launchTitle = pivotLaunchTitle(pivot);
          return (
            <li
              key={`lead-pivot-${i}-${label}`}
              className="flex items-center gap-2.5 rounded-lg border border-slate-800/80 bg-slate-950/50 px-2.5 py-2"
            >
              <span className="shrink-0 text-base leading-none" aria-hidden>
                {icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-slate-100">{label}</p>
                <p className="truncate text-xs text-slate-400">{description}</p>
              </div>
              <button
                type="button"
                title={launchTitle}
                aria-label={launchTitle}
                onClick={() => onPivotLead && onPivotLead(pivot)}
                className={PIVOT_CTA_CLASS}
                data-testid="phone-discovery-pivot"
              >
                ↗ {action}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
